import { NotesPanel, ProtectedComponent } from '@components'

import {
  Button,
  ButtonDropdown,
  ButtonsGroup,
  ButtonVariant,
  NavItem,
  SkeletonButton,
  SkeletonIconButton,
  Space,
  Tooltip,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Link, useLocation } from 'react-router'

import { VoidBillConfirmationModal as VoidBillConfirmationModalComponent } from '@features/bills/elements/VoidBillConfirmationModal'
import { BillState } from '@features/bills/enums/billState'
import { useRegisterBillPayment } from '@features/bills/hooks/useRegisterBillPayment'
import { useSubjectReconciliationSuggestions } from '@features/paymentReconciliationModal/hooks'
import { useCurrentUser } from '@modules-deprecated/app/user/currentUserContext'
import { isBillDeletable, isBillVoidable } from '@views/bills/utils/isBillDeletable'

import { OPEN_NOTES_PANEL_SHORTCUT } from '../../../../constants/shortcuts'
import { CustomEvent } from '../../../../enums/customEvent'
import { ModalId } from '../../../../enums/modalId'
import { QueryKeys } from '../../../../enums/queryKeys'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { useCustomEventListener } from '../../../../hooks/useCustomEventListener'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useBillTimeline } from '../../hooks/useBillTimeline'
import { BillNotesPanel } from '../BillNotesPanel'
import { useBillView } from '../BillReadableForm'
import { BillReconciliationModal as BillReconciliationModalComponent } from '../BillReconciliationModal'
import { VoidStaticActionButton } from '../VoidStaticActionButton'
import { useCloseReconciliationModals } from './hooks/useCloseReconcilliationModals'

enum ReadableBillActionId {
  Duplicate = 'duplicate',
  Credit = 'credit',
  Void = 'void',
}
const actionToSegmentDescription: Record<ReadableBillActionId, string> = {
  [ReadableBillActionId.Duplicate]: 'duplicate',
  [ReadableBillActionId.Credit]: 'credit bill',
  [ReadableBillActionId.Void]: 'void bill',
}

const BillReconciliationModal = withModalConditionalRender(BillReconciliationModalComponent)
const VoidBillConfirmationModal = withModalConditionalRender(VoidBillConfirmationModalComponent)

export const BillReadableActions = (): ReactElement => {
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState(false)
  const { t } = useTranslation()
  const { track } = useSegment()
  const { isLoading: isLoadingUserData } = useCurrentUser()
  const { billId, billData, isFetching, isVoided } = useBillView()

  const queryClient = useQueryClient()
  const location = useLocation()
  const { open: openVoidConfirmationModal } = useModal(ModalId.VoidBillConfirmationModal)
  const bill = useMemo(() => billData?.bill, [billData?.bill])
  const { isLoading: isLoadingMatchSuggestions, subjectHasMatchSuggestion } = useSubjectReconciliationSuggestions(bill)

  const { open: openBillReconciliationModal } = useModal(ModalId.BillReconciliationModal)

  const { handleRegisterPayment } = useRegisterBillPayment({
    bill,
    openReconciliationModal: openBillReconciliationModal,
  })

  const { count: notesCount } = useBillTimeline(billId)

  const isLoading = isFetching || isLoadingUserData
  const isBillApprovedAndNotPaid = bill?.state === BillState.Approved && !bill?.isPaid
  const shouldRenderMoreButton = isLoading || (!isLoading && !isVoided)
  const shouldRenderRegisterPaymentButton = isLoading || (!isLoading && isBillApprovedAndNotPaid)
  const registerPaymentButtonVariant: ButtonVariant = subjectHasMatchSuggestion ? 'tonal' : 'filled'
  useCloseReconciliationModals()

  // Handle ember's Register Payment success state
  useCustomEventListener(CustomEvent.PaymentSaved, () => {
    queryClient.invalidateQueries([QueryKeys.Bill, billId])
  })

  const { url: duplicateUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getDuplicateRoute(billId))
  const { url: createCreditNoteUrl } = useOrganizationViewUrl(
    reactRoute.organization.bills.getCreateCreditNoteRoute(billId),
  )

  const isDeletable = isBillDeletable({
    bill,
    integrationStatus: bill?.integrationStatus,
  })
  const isVoidable = isBillVoidable({
    bill,
    integrationStatus: bill?.integrationStatus,
  })

  const isVoidDeleteButtonDisabled = !isDeletable && !isVoidable

  const moreButtonItems: NavItem<ReadableBillActionId>[] = useMemo(
    () => [
      {
        id: ReadableBillActionId.Duplicate,
        children: t('bill.read.more.duplicate'),
        to: `${duplicateUrl}${location.search}`,
        value: ReadableBillActionId.Duplicate,
      },
      {
        id: ReadableBillActionId.Credit,
        children: t('bill.read.more.credit'),
        to: `${createCreditNoteUrl}${location.search}`,
        value: ReadableBillActionId.Credit,
      },
      {
        id: ReadableBillActionId.Void,
        children: (
          <VoidStaticActionButton
            isSyncedWithCPA={isVoidDeleteButtonDisabled}
            toolTipMessage={t('bill.read.more.void.synced_to_cpa')}
          />
        ),
        value: ReadableBillActionId.Void,
        readonly: isVoidDeleteButtonDisabled,
      },
    ],
    [createCreditNoteUrl, duplicateUrl, isVoidDeleteButtonDisabled, location.search, t],
  )

  const handleMoreSelect = useCallback(
    (_: string, value: ReadableBillActionId) => {
      track('XXX - Jesper - Action Taken', {
        context: 'single_bill_view',
        action: `more: ${actionToSegmentDescription[value]}`,
      })

      if (value === ReadableBillActionId.Void) {
        openVoidConfirmationModal()
      }
    },
    [openVoidConfirmationModal, track],
  )

  const handleAddNotesButtonClick = useCallback(() => {
    setIsNotesPanelOpen(true)
  }, [])

  const handleNotesPanelClose = useCallback(() => {
    setIsNotesPanelOpen(false)
  }, [])

  const handleOpenNotesPanelShortcut = useCallback(() => {
    setIsNotesPanelOpen(true)
  }, [])

  const handleEditLinkClick = useCallback(() => {
    track('XXX - Jesper - Action Taken', {
      context: 'single_bill_view',
      action: 'more: edit',
    })
  }, [track])

  useHotkeys(
    OPEN_NOTES_PANEL_SHORTCUT,
    handleOpenNotesPanelShortcut,
    { enabled: !isLoading, enableOnTags: ['INPUT', 'SELECT', 'TEXTAREA'] },
    [isLoading, handleOpenNotesPanelShortcut],
  )

  const { url: editBillUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getEditBillRoute(billId))

  return (
    <>
      <ButtonsGroup>
        {shouldRenderRegisterPaymentButton &&
          (isLoading ? (
            <SkeletonButton width={172} />
          ) : (
            <Button
              icon="handCoins"
              onClick={handleRegisterPayment}
              disabled={isLoadingMatchSuggestions}
              variant={registerPaymentButtonVariant}
            >
              {t('bill.register_payment.button.register')}
            </Button>
          ))}
        {!isVoided && (
          <ProtectedComponent scopes={Scope.BillWrite}>
            <Space size="s12" horizontal />
            <Link
              onClick={handleEditLinkClick}
              to={{
                pathname: editBillUrl,
                search: location.search,
              }}
            >
              <Button icon="squarePen" variant="outlined" disabled={isFetching}>
                {t('bill.view.edit_bill')}
              </Button>
            </Link>
          </ProtectedComponent>
        )}
        {isLoading ? (
          <SkeletonIconButton />
        ) : (
          <Tooltip label={t('note_panel.title')} placement="bottom">
            <NotesPanel.Trigger onClick={handleAddNotesButtonClick} count={notesCount} />
          </Tooltip>
        )}
        {shouldRenderMoreButton &&
          (isLoading ? (
            <SkeletonButton width={92} />
          ) : (
            <ButtonDropdown items={moreButtonItems} onSelect={handleMoreSelect} variant="outlined">
              {t('more')}
            </ButtonDropdown>
          ))}
      </ButtonsGroup>

      {/* Bill Reconciliation Modal */}
      {bill && <BillReconciliationModal subject={bill} id={ModalId.BillReconciliationModal} />}

      {/* Void Confirmation Modal  */}
      <VoidBillConfirmationModal billId={billId} id={ModalId.VoidBillConfirmationModal} />

      {/* Notes Panel */}
      <BillNotesPanel billId={billId} isOpen={isNotesPanelOpen} onClose={handleNotesPanelClose} />
    </>
  )
}
